import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  clearWorkspaceMemoryLimit,
  readOrganizationMemory,
  setWorkspaceMemoryLimit,
} from '@/lib/api/generated/api';
import type {
  Error as ApiError,
  OrganizationMemoryView,
  SetMemoryLimitRequest,
} from '@/lib/api/generated/model';

import { organizationKeys } from '../queryKeys';

/**
 * Every workspace's memory amount, in one read (spec 149).
 *
 * `null` from a service that predates the read (404): the memory parts are then simply
 * not shown, rather than failing a page whose other limits still work. No polling,
 * unlike concurrency's queue refresh: a memory amount is a standing rule with no live
 * counter beside it.
 */
export function useOrganizationMemory() {
  return useQuery<OrganizationMemoryView | null>({
    queryKey: organizationKeys.memory(),
    queryFn: async ({ signal }) => {
      const response = await readOrganizationMemory({ signal });
      if (response.status === 200) return response.data;
      if ((response.status as number) === 404) return null;
      throw new Error('Could not read how much memory each workspace may use.');
    },
    refetchOnWindowFocus: false,
  });
}

/**
 * Why a memory change was refused, in the service's own words where it has them.
 *
 * Takes the response body because a 422 carries the numbers — the minimum and the
 * maximum are the service's, not restated here (FR-004a).
 */
export function memoryError(status: number, body: ApiError | undefined): Error {
  if (status === 403) {
    return new Error(
      'Only an organization administrator can change how much memory a workspace may use.',
    );
  }
  if (status === 422) {
    return new Error(
      body?.message ?? 'That memory amount is outside what is allowed.',
    );
  }
  if (status === 404) {
    return new Error('That workspace no longer exists.');
  }
  return new Error('Could not save the memory amount.');
}

/**
 * A memory change moves every workspace that resolves through the default, which a
 * mutation response does not describe — so the read is discarded, never patched.
 */
export function useInvalidateMemory() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: organizationKeys.memory() });
}

export interface MemoryLimitTarget {
  workspaceId: string;
}

export function useSetMemoryLimit() {
  const invalidate = useInvalidateMemory();
  return useMutation({
    mutationFn: async ({
      workspaceId,
      ...request
    }: MemoryLimitTarget & SetMemoryLimitRequest) => {
      const response = await setWorkspaceMemoryLimit(workspaceId, request);
      if (response.status !== 200) {
        throw memoryError(response.status, bodyOf(response));
      }
    },
    onSuccess: invalidate,
  });
}

export function useClearMemoryLimit() {
  const invalidate = useInvalidateMemory();
  return useMutation({
    mutationFn: async ({ workspaceId }: MemoryLimitTarget) => {
      const response = await clearWorkspaceMemoryLimit(workspaceId);
      if (response.status !== 204) {
        throw memoryError(response.status, bodyOf(response));
      }
    },
    onSuccess: invalidate,
  });
}

/** The `Error` envelope when the response carries one; the clear route's 422 does not. */
function bodyOf(response: { data: unknown }): ApiError | undefined {
  const data = response.data;
  return typeof data === 'object' && data !== null && 'error' in data
    ? (data as ApiError)
    : undefined;
}
